import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { httpsCallable } from 'firebase/functions'
import { Layout } from '../components/layout/Layout'
import { ManagedSeo } from '../components/seo/ManagedSeo'
import { assets } from '../config/assets'
import { functions } from '../lib/functions'

interface ProgramLeadPayload {
  firstName: string
  lastName: string
  email: string
  phone: string
  program: string
}

interface CheckoutResult {
  url: string | null
}

const submitProgramLead = httpsCallable<ProgramLeadPayload, CheckoutResult>(
  functions,
  'submitProgramLead',
)

const CATALOG = [
  {
    slug: 'confident-equestrian',
    title: 'Confident Equestrian Foundation Guide',
    description:
      'A structured system for building lasting confidence in the saddle and on the ground.',
    price: '$497',
    image: assets.products.confidentEquestrianBox,
    link: '/programs/confident-equestrian',
  },
  {
    slug: 'riders-foundation-guide',
    title: 'Riders Foundation Guide',
    description:
      'The foundational framework every rider needs before anything else makes sense.',
    price: '$197',
    image: assets.products.ridersFoundation,
    link: '/programs/riders-foundation-guide',
  },
  {
    slug: 'groundwork-foundation-guide',
    title: 'Groundwork Foundation Guide',
    description:
      'Build communication and trust from the ground up before you ever get in the saddle.',
    price: '$147',
    image: assets.products.foundationGroundwork,
    link: '/programs/groundwork-foundation-guide',
  },
  {
    slug: 'hindquarter-engagement',
    title: 'Hindquarter Engagement Foundation Guide',
    description:
      'Targeted exercises for engagement, balance, and collection. Unlock your horse\'s engine.',
    price: '$19.97',
    image: assets.products.foundationHindquarter,
    link: null,
  },
  {
    slug: 'liberty-foundations',
    title: 'Liberty Foundations: Unlocking the Start',
    description:
      'The foundational steps for working with your horse at liberty. Build connection without a lead rope.',
    price: '$19.97',
    image: assets.products.libertyFoundations,
    link: '/programs/liberty-foundations',
  },
  {
    slug: 'fitness-equestrians',
    title: 'Fitness for Equestrians',
    description:
      'A four-week workout program built specifically for riders. Tells you exactly what to do each week to build the strength and balance your riding demands.',
    price: '$14.97',
    image: assets.products.fitnessBox,
    link: null,
  },
]

const TRUST_SIGNALS = [
  'Instant access after purchase',
  'Lifetime access to all materials',
  '30-day money back guarantee',
]

type FormStatus = 'idle' | 'loading' | 'error'


export function ProgramsPage() {
  const [modalOpen, setModalOpen] = useState(false)
  const [activeProgram, setActiveProgram] = useState<(typeof CATALOG)[number] | null>(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<FormStatus>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  function openModal(program: (typeof CATALOG)[number]) {
    setActiveProgram(program)
    setModalOpen(true)
    setStatus('idle')
    setErrorMessage('')
  }

  function closeModal() {
    setModalOpen(false)
    setActiveProgram(null)
    setFirstName('')
    setLastName('')
    setEmail('')
    setStatus('idle')
    setErrorMessage('')
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!activeProgram) return
    setStatus('loading')
    setErrorMessage('')

    try {
      const result = await submitProgramLead({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: '',
        program: activeProgram.slug,
      })
      const url = result.data.url
      if (url) {
        window.location.href = url
      }
    } catch {
      setStatus('error')
      setErrorMessage(
        'Something went wrong. Please try again or email support@advantagehorsemanship.com.',
      )
    }
  }

  const isDisabled = status === 'loading' || !firstName.trim() || !lastName.trim() || !email.trim()

  return (
    <Layout>
      <ManagedSeo path="/programs" />
      {/* ─── HEADER ───────────────────────────────────────────────────────── */}
      <section className="bg-navy py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-5xl sm:text-6xl text-white mb-4">
            Horse Training Guides &amp; Coaching Programs
          </h1>
          <p className="font-body text-lg text-white/70 max-w-2xl mx-auto">
            Focused guides built around Scott's methodology. Buy once, access
            forever.
          </p>
        </div>
      </section>

      {/* ─── CATALOG ──────────────────────────────────────────────────────── */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid auto-rows-fr sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {CATALOG.map((program) => (
              <div
                key={program.slug}
                className="h-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm flex flex-col"
              >
                {/* Product image */}
                <div
                  className="h-[280px] sm:h-[300px] flex items-center justify-center p-6"
                  style={{ backgroundColor: '#0A1628' }}
                >
                  {program.link ? (
                    <Link
                      to={program.link}
                      className="w-full h-full flex items-center justify-center cursor-pointer"
                    >
                      <img
                        src={program.image}
                        alt={`${program.title} box mockup`}
                        width={240}
                        height={300}
                        loading="lazy"
                        className="w-full h-full object-contain"
                      />
                    </Link>
                  ) : (
                    <img
                      src={program.image}
                      alt={`${program.title} box mockup`}
                      width={240}
                      height={300}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-heading text-2xl text-navy mb-3">
                    {program.link ? (
                      <Link
                        to={program.link}
                        className="text-navy hover:underline cursor-pointer"
                      >
                        {program.title}
                      </Link>
                    ) : (
                      program.title
                    )}
                  </h3>
                  <p className="font-body text-sm text-navy/70 leading-relaxed mb-6 flex-1">
                    {program.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-3xl text-navy font-bold">
                      {program.price}
                    </span>
                    <button
                      onClick={() => openModal(program)}
                      style={{
                        backgroundColor: '#C8A84B',
                        color: '#0A1628',
                        fontWeight: '700',
                        padding: '12px 24px',
                        fontSize: '14px',
                        border: 'none',
                        cursor: 'pointer',
                        minHeight: '52px',
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Buy Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CREDIBILITY STRIP ────────────────────────────────────────────── */}
      <section className="py-12" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            {TRUST_SIGNALS.map((signal) => (
              <div key={signal} className="flex items-center justify-center gap-3">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                  className="flex-shrink-0"
                >
                  <circle cx="10" cy="10" r="9" stroke="#C8A84B" strokeWidth="1.5" />
                  <path
                    d="M6.5 10l2.5 2.5 4.5-5"
                    stroke="#C8A84B"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="font-body text-sm font-semibold text-navy">
                  {signal}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA STRIP ────────────────────────────────────────────────────── */}
      <section className="bg-navy py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-body text-lg text-white/80 mb-4">
            Looking for deeper coaching?
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/horse-sense"
              className="font-body text-sm font-semibold text-gold hover:text-white transition-colors underline underline-offset-4"
            >
              Horse Sense Coaching Program
            </Link>
            <span className="text-white/30 hidden sm:inline">&middot;</span>
            <Link
              to="/accelerator"
              className="font-body text-sm font-semibold text-gold hover:text-white transition-colors underline underline-offset-4"
            >
              Confidence Accelerator Class
            </Link>
          </div>
        </div>
      </section>

      {/* ─── LEAD CAPTURE MODAL ───────────────────────────────────────────── */}
      {modalOpen && activeProgram && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal()
          }}
        >
          <div className="bg-white w-full max-w-md">
            <div className="bg-navy px-8 py-6 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-2xl text-white">
                  Buy Now
                </h2>
                <p className="font-body text-xs text-gold mt-1">
                  {activeProgram.title}, {activeProgram.price}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-white/60 hover:text-white transition-colors ml-4 flex-shrink-0"
                aria-label="Close"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-8 py-8">
              <p className="font-body text-sm text-navy/70 mb-6">
                Enter your details and you'll be taken directly to a secure
                checkout.
              </p>

              <div className="space-y-4 mb-6">
                <div>
                  <label
                    className="block font-body text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1"
                    htmlFor="prog-first-name"
                  >
                    First Name *
                  </label>
                  <input
                    id="prog-first-name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                    className="w-full border border-navy/20 px-4 py-3 font-body text-navy focus:outline-none focus:border-navy"
                    style={{ fontSize: '16px' }}
                    placeholder="Your first name"
                  />
                </div>

                <div>
                  <label
                    className="block font-body text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1"
                    htmlFor="prog-last-name"
                  >
                    Last Name *
                  </label>
                  <input
                    id="prog-last-name"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    autoComplete="family-name"
                    className="w-full border border-navy/20 px-4 py-3 font-body text-navy focus:outline-none focus:border-navy"
                    style={{ fontSize: '16px' }}
                    placeholder="Your last name"
                  />
                </div>

                <div>
                  <label
                    className="block font-body text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1"
                    htmlFor="prog-email"
                  >
                    Email Address *
                  </label>
                  <input
                    id="prog-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full border border-navy/20 px-4 py-3 font-body text-navy focus:outline-none focus:border-navy"
                    style={{ fontSize: '16px' }}
                    placeholder="your@email.com"
                  />
                </div>

              </div>

              {status === 'error' && errorMessage && (
                <p className="font-body text-sm text-red-600 mb-4">
                  {errorMessage}
                </p>
              )}

              <button
                type="submit"
                disabled={isDisabled}
                style={{
                  backgroundColor: isDisabled ? '#a08940' : '#C8A84B',
                  color: '#0A1628',
                  fontWeight: '700',
                  padding: '16px 32px',
                  fontSize: '16px',
                  border: 'none',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  width: '100%',
                  minHeight: '52px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                {status === 'loading'
                  ? 'Redirecting to checkout...'
                  : 'Proceed to Secure Checkout \u2192'}
              </button>

              <p className="font-body text-xs text-navy/40 mt-3 text-center">
                Powered by Stripe &middot; Secure checkout
              </p>
            </form>
          </div>
        </div>
      )}
    </Layout>
  )
}
